import React, { createContext, useContext, useState, useEffect } from 'react';

interface AppThemeContextType {
  theme: 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark') => void;
  toggleTheme: () => void;
  sidebarExpanded: boolean;
  setSidebarExpanded: (expanded: boolean) => void;
  toggleSidebar: () => void;
  sidebarVisible: boolean;
  setSidebarVisible: (visible: boolean) => void;
  collapseSidebar: () => void;
}

const AppThemeContext = createContext<AppThemeContextType | undefined>(undefined);

export const useAppTheme = () => {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within AppThemeProvider');
  }
  return context;
};

export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

interface AppThemeProviderProps {
  children: React.ReactNode;
}

export const AppThemeProvider: React.FC<AppThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const savedTheme = localStorage.getItem('stonkfu-theme');
    return (savedTheme === 'dark' ? 'dark' : 'light');
  });

  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Apply theme class to body
    if (theme === 'dark') {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
    
    // Save to localStorage
    localStorage.setItem('stonkfu-theme', theme);
  }, [theme]);

  useEffect(() => {
    // On mobile, sidebar should be hidden by default
    if (isMobile) {
      setSidebarVisible(false);
      setSidebarExpanded(true); // Always expanded when visible on mobile
    } else {
      // Desktop: Check saved preference or default to visible
      const savedSidebarState = localStorage.getItem('sidebarVisible');
      setSidebarVisible(savedSidebarState !== 'false');
      setSidebarExpanded(false); // Condensed by default on desktop
    }
  }, [isMobile]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const toggleSidebar = () => {
    if (isMobile) {
      // Mobile: Simple show/hide
      setSidebarVisible(!sidebarVisible);
    } else {
      // Desktop: Complex behavior
      if (!sidebarVisible) {
        // If hidden, show it condensed
        setSidebarVisible(true);
        setSidebarExpanded(false);
        localStorage.setItem('sidebarVisible', 'true');
      } else if (sidebarExpanded) {
        // If expanded, collapse to condensed first
        setSidebarExpanded(false);
      } else {
        // If condensed, hide it
        setSidebarVisible(false);
        localStorage.setItem('sidebarVisible', 'false');
      }
    }
  };

  const collapseSidebar = () => {
    // Used when clicking outside - collapse to condensed state
    if (sidebarExpanded && !isMobile) {
      setSidebarExpanded(false);
    }
  };

  const value: AppThemeContextType = {
    theme,
    setTheme,
    toggleTheme,
    sidebarExpanded,
    setSidebarExpanded,
    toggleSidebar,
    sidebarVisible,
    setSidebarVisible,
    collapseSidebar,
  };

  return (
    <AppThemeContext.Provider value={value}>
      {children}
    </AppThemeContext.Provider>
  );
};